import {
  Edit as EditIcon,
  People as PeopleIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  Payment as PaymentIcon
} from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  Tooltip
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import { useAnalytics } from '../../contexts/AnalyticsContext'
import {
  createSubscription,
  getOfficeSubscription,
  updateSubscription
} from '../../db/db'
import { AdminService } from '../../services/AdminService'
import { AnalyticsEvents } from '../../types/AnalyticsEvents'
import Database from '../../types/Database'
import { getRoleColor } from '../../utils/rolePermissions'

import ConfirmationDialog from './ConfirmationDialog'

/**
 * OfficeConfigManagement Component
 *
 * Displays office list and allows administrators to modify desk capacity and subscription tier.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.5, 10.6, 10.7, 10.8, 10.9, 10.10
 */

export interface OfficeConfigManagementProps {
  currentUserId: string
  currentUserEmail: string
  onError?: (error: string) => void
  onSuccess?: (message: string) => void
}

interface EditState {
  office: Database.Office | null
  capacity: string
  tier: Database.SubscriptionTier
  capacityError: string
}

interface OfficeDetailsCache {
  [officeId: string]: {
    ownerEmail: string | null
    billingInterval: string | null
    renewalDate: string | null
  }
}

interface AccessListEntry {
  user: Database.User
  role: 'owner' | 'manager' | 'member'
}

interface SubscriptionFormState {
  office: Database.Office | null
  existingSubscription: Database.Subscription | null
  tier: Database.SubscriptionTier
  status: Database.SubscriptionStatus
  billingInterval: 'monthly' | 'annual'
  currentPeriodStart: string
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
  stripeSubscriptionId: string
  stripeCustomerId: string
  deskCapacity: string
}

const TIER_CAPACITIES: Record<Database.SubscriptionTier, number> = {
  free: 5,
  small: 20,
  medium: 50,
  large: 200
}

const OfficeConfigManagement: React.FC<OfficeConfigManagementProps> = ({
  currentUserId,
  currentUserEmail,
  onError,
  onSuccess
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { trackEvent } = useAnalytics()

  const [offices, setOffices] = useState<Database.Office[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isOperating, setIsOperating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [officeDetails, setOfficeDetails] = useState<OfficeDetailsCache>({})

  // Access list dialog state
  const [accessDialogOpen, setAccessDialogOpen] = useState(false)
  const [accessDialogOffice, setAccessDialogOffice] =
    useState<Database.Office | null>(null)
  const [accessList, setAccessList] = useState<AccessListEntry[]>([])
  const [isLoadingAccess, setIsLoadingAccess] = useState(false)

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editState, setEditState] = useState<EditState>({
    office: null,
    capacity: '',
    tier: 'free',
    capacityError: ''
  })

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    message: string
    onConfirm: () => void
    severity: 'warning' | 'error' | 'info'
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    severity: 'warning'
  })

  // Subscription dialog state
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false)
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false)
  const [subscriptionForm, setSubscriptionForm] =
    useState<SubscriptionFormState>({
      office: null,
      existingSubscription: null,
      tier: 'small',
      status: 'active',
      billingInterval: 'monthly',
      currentPeriodStart: '',
      currentPeriodEnd: '',
      cancelAtPeriodEnd: false,
      stripeSubscriptionId: '',
      stripeCustomerId: '',
      deskCapacity: '20'
    })

  useEffect(() => {
    loadOffices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadOffices = async () => {
    setIsLoading(true)
    setError(null)
    try {
      // Fetch offices directly
      const db = (await import('../../db/db')).default
      const allOffices = await db.query({
        collection: 'offices',
        limit: 10000
      })
      setOffices(allOffices)

      // Load subscription details for each office
      const details: OfficeDetailsCache = {}
      await Promise.all(
        allOffices.map(async office => {
          if (!office.oid) return
          try {
            const result = await AdminService.getOfficeSubscriptionDetails(
              office.oid
            )
            let renewalDate: string | null = null
            if (result.subscription?.currentPeriodEnd) {
              const raw = result.subscription.currentPeriodEnd as unknown
              if (
                raw &&
                typeof raw === 'object' &&
                'seconds' in (raw as object)
              ) {
                // Firestore Timestamp object
                renewalDate = new Date(
                  (raw as { seconds: number }).seconds * 1000
                ).toLocaleDateString()
              } else if (raw instanceof Date) {
                renewalDate = raw.toLocaleDateString()
              } else {
                renewalDate = new Date(
                  raw as string | number
                ).toLocaleDateString()
              }
              if (renewalDate === 'Invalid Date') {
                renewalDate = null
              }
            }
            details[office.oid] = {
              ownerEmail: result.ownerEmail,
              billingInterval: result.subscription?.billingInterval || null,
              renewalDate
            }
          } catch {
            details[office.oid!] = {
              ownerEmail: null,
              billingInterval: null,
              renewalDate: null
            }
          }
        })
      )
      setOfficeDetails(details)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load offices'
      setError(errorMessage)
      if (onError) onError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleDeleteClick = (office: Database.Office) => {
    const isDeleted = !!office.isDeleted

    setConfirmDialog({
      open: true,
      title: isDeleted ? 'Restore Office' : 'Delete Office',
      message: isDeleted
        ? `Are you sure you want to restore "${office.name}"? It will become visible to all users again.`
        : `Are you sure you want to delete "${office.name}"? It will be hidden from all users but can be restored later.`,
      onConfirm: () => confirmToggleDelete(office, !isDeleted),
      severity: isDeleted ? 'info' : 'warning'
    })
  }

  const confirmToggleDelete = async (office: Database.Office, markAsDeleted: boolean) => {
    setConfirmDialog({ ...confirmDialog, open: false })
    setIsOperating(true)

    try {
      if (!office.oid) {
        throw new Error('Office ID is missing')
      }

      await AdminService.toggleOfficeDeleted(office.oid, markAsDeleted, currentUserId)

      // Log the admin action
      await AdminService.logAdminAction(
        currentUserId,
        currentUserEmail,
        markAsDeleted ? 'user_deleted' : 'user_promoted', // Reusing existing action types for audit
        office.oid,
        undefined,
        {
          previousValue: !markAsDeleted,
          newValue: markAsDeleted,
          reason: markAsDeleted ? 'Office soft-deleted by admin' : 'Office restored by admin'
        }
      )

      // Update local state
      setOffices(prevOffices =>
        prevOffices.map(o =>
          o.oid === office.oid
            ? { ...o, isDeleted: markAsDeleted }
            : o
        )
      )

      const message = markAsDeleted
        ? `Successfully deleted office "${office.name}". It is now hidden from users.`
        : `Successfully restored office "${office.name}". It is now visible to users.`
      setSuccessMessage(message)
      if (onSuccess) onSuccess(message)
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : `Failed to ${markAsDeleted ? 'delete' : 'restore'} office`
      setError(errorMessage)
      if (onError) onError(errorMessage)
    } finally {
      setIsOperating(false)
    }
  }

  const handleEditClick = (office: Database.Office) => {
    setEditState({
      office,
      capacity: String(office.userCapacity || 0),
      tier: office.subscriptionTier || 'free',
      capacityError: ''
    })
    setEditDialogOpen(true)
  }

  const handleEditDialogClose = () => {
    setEditDialogOpen(false)
    setEditState({
      office: null,
      capacity: '',
      tier: 'free',
      capacityError: ''
    })
  }

  const handleViewAccessClick = async (office: Database.Office) => {
    if (!office.oid) return
    setAccessDialogOffice(office)
    setAccessDialogOpen(true)
    setIsLoadingAccess(true)
    try {
      const list = await AdminService.getOfficeAccessList(office.oid, office)
      setAccessList(list)
    } catch {
      setAccessList([])
    } finally {
      setIsLoadingAccess(false)
    }
  }

  const handleAccessDialogClose = () => {
    setAccessDialogOpen(false)
    setAccessDialogOffice(null)
    setAccessList([])
  }

  const validateCapacity = (value: string): boolean => {
    const num = Number(value)
    if (!Number.isInteger(num) || num <= 0) {
      setEditState(prev => ({
        ...prev,
        capacityError: 'Capacity must be a positive integer'
      }))
      return false
    }
    setEditState(prev => ({ ...prev, capacityError: '' }))
    return true
  }

  const handleCapacityChange = (value: string) => {
    setEditState(prev => ({ ...prev, capacity: value }))
    validateCapacity(value)
  }

  const handleTierChange = (tier: Database.SubscriptionTier) => {
    setEditState(prev => ({ ...prev, tier }))
  }

  const handleSaveClick = () => {
    if (!editState.office) return

    // Validate capacity
    if (!validateCapacity(editState.capacity)) {
      return
    }

    const newCapacity = Number(editState.capacity)
    const oldCapacity = editState.office.userCapacity || 0
    const oldTier = editState.office.subscriptionTier || 'free'
    const newTier = editState.tier

    const capacityChanged = newCapacity !== oldCapacity
    const tierChanged = newTier !== oldTier

    if (!capacityChanged && !tierChanged) {
      handleEditDialogClose()
      return
    }

    let message = `Are you sure you want to update ${editState.office.name}?`
    const changes: string[] = []
    if (capacityChanged) {
      changes.push(`Capacity: ${oldCapacity} to ${newCapacity}`)
    }
    if (tierChanged) {
      changes.push(`Tier: ${oldTier} to ${newTier}`)
    }
    message += ` Changes: ${changes.join(', ')}`

    setConfirmDialog({
      open: true,
      title: 'Confirm Office Configuration Changes',
      message,
      onConfirm: () => confirmSave(capacityChanged, tierChanged),
      severity: 'warning'
    })
  }

  const confirmSave = async (
    capacityChanged: boolean,
    tierChanged: boolean
  ) => {
    setConfirmDialog({ ...confirmDialog, open: false })
    setEditDialogOpen(false)
    setIsOperating(true)

    try {
      if (!editState.office || !editState.office.oid) {
        throw new Error('Office ID is missing')
      }

      const officeId = editState.office.oid
      const newCapacity = Number(editState.capacity)
      const newTier = editState.tier
      const oldCapacity = editState.office.userCapacity || 0
      const oldTier = editState.office.subscriptionTier || 'free'

      // Update capacity if changed
      if (capacityChanged) {
        await AdminService.updateOfficeCapacity(officeId, newCapacity)
        await AdminService.logAdminAction(
          currentUserId,
          currentUserEmail,
          'office_capacity_changed',
          officeId,
          undefined,
          {
            previousValue: oldCapacity,
            newValue: newCapacity
          }
        )

        // Track analytics event for capacity change
        try {
          trackEvent(AnalyticsEvents.ADMIN_OFFICE_CONFIG, {
            action_type: 'update_office_capacity'
          })
        } catch (error) {
          console.error('Failed to track admin office config event:', error)
        }
      }

      // Update tier if changed
      if (tierChanged) {
        await AdminService.updateOfficeTier(officeId, newTier)
        await AdminService.logAdminAction(
          currentUserId,
          currentUserEmail,
          'office_tier_changed',
          officeId,
          undefined,
          {
            previousValue: oldTier,
            newValue: newTier
          }
        )

        // Track analytics event for tier change
        try {
          trackEvent(AnalyticsEvents.ADMIN_OFFICE_CONFIG, {
            action_type: 'update_office_tier'
          })
        } catch (error) {
          console.error('Failed to track admin office config event:', error)
        }
      }

      // Update local state
      setOffices(prevOffices =>
        prevOffices.map(o =>
          o.oid === officeId
            ? {
                ...o,
                userCapacity: capacityChanged ? newCapacity : o.userCapacity,
                subscriptionTier: tierChanged ? newTier : o.subscriptionTier
              }
            : o
        )
      )

      const message = `Successfully updated office configuration for ${editState.office.name}`
      setSuccessMessage(message)
      if (onSuccess) onSuccess(message)
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update office configuration'
      setError(errorMessage)
      if (onError) onError(errorMessage)
    } finally {
      setIsOperating(false)
      handleEditDialogClose()
    }
  }

  const handleSubscriptionClick = async (office: Database.Office) => {
    if (!office.oid) return
    setSubscriptionDialogOpen(true)
    setIsLoadingSubscription(true)

    try {
      const subscription = await getOfficeSubscription(office.oid)
      const today = new Date().toISOString().split('T')[0] ?? ''

      if (subscription) {
        // Editing existing subscription
        let startDate = today
        let endDate = today
        if (subscription.currentPeriodStart) {
          const raw = subscription.currentPeriodStart as unknown
          if (raw && typeof raw === 'object' && 'seconds' in (raw as object)) {
            startDate =
              new Date((raw as { seconds: number }).seconds * 1000)
                .toISOString()
                .split('T')[0] ?? ''
          } else if (raw instanceof Date) {
            startDate = raw.toISOString().split('T')[0] ?? ''
          }
        }
        if (subscription.currentPeriodEnd) {
          const raw = subscription.currentPeriodEnd as unknown
          if (raw && typeof raw === 'object' && 'seconds' in (raw as object)) {
            endDate =
              new Date((raw as { seconds: number }).seconds * 1000)
                .toISOString()
                .split('T')[0] ?? ''
          } else if (raw instanceof Date) {
            endDate = raw.toISOString().split('T')[0] ?? ''
          }
        }

        setSubscriptionForm({
          office,
          existingSubscription: subscription,
          tier: subscription.tier,
          status: subscription.status,
          billingInterval: subscription.billingInterval || 'monthly',
          currentPeriodStart: startDate,
          currentPeriodEnd: endDate,
          cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
          stripeSubscriptionId: subscription.stripeSubscriptionId || '',
          stripeCustomerId: subscription.stripeCustomerId || '',
          deskCapacity: String(
            office.userCapacity || TIER_CAPACITIES[subscription.tier]
          )
        })
      } else {
        // Creating new subscription
        const oneYearFromNow = new Date()
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1)
        const defaultTier = office.subscriptionTier || 'small'

        setSubscriptionForm({
          office,
          existingSubscription: null,
          tier: defaultTier,
          status: 'active',
          billingInterval: 'monthly',
          currentPeriodStart: today,
          currentPeriodEnd: oneYearFromNow.toISOString().split('T')[0] ?? '',
          cancelAtPeriodEnd: false,
          stripeSubscriptionId: '',
          stripeCustomerId: '',
          deskCapacity: String(TIER_CAPACITIES[defaultTier])
        })
      }
    } catch {
      setSubscriptionForm(prev => ({
        ...prev,
        office,
        existingSubscription: null
      }))
    } finally {
      setIsLoadingSubscription(false)
    }
  }

  const handleSubscriptionDialogClose = () => {
    setSubscriptionDialogOpen(false)
    setSubscriptionForm({
      office: null,
      existingSubscription: null,
      tier: 'small',
      status: 'active',
      billingInterval: 'monthly',
      currentPeriodStart: '',
      currentPeriodEnd: '',
      cancelAtPeriodEnd: false,
      stripeSubscriptionId: '',
      stripeCustomerId: '',
      deskCapacity: '20'
    })
  }

  const handleSaveSubscription = async () => {
    if (!subscriptionForm.office || !subscriptionForm.office.oid) return

    setIsOperating(true)
    setSubscriptionDialogOpen(false)

    try {
      const officeId = subscriptionForm.office.oid
      const ownerId = subscriptionForm.office.uid || currentUserId

      if (subscriptionForm.existingSubscription) {
        // Update existing subscription
        const updateData: Parameters<typeof updateSubscription>[1] = {
          tier: subscriptionForm.tier,
          status: subscriptionForm.status,
          billingInterval: subscriptionForm.billingInterval,
          currentPeriodStart: new Date(subscriptionForm.currentPeriodStart),
          currentPeriodEnd: new Date(subscriptionForm.currentPeriodEnd),
          cancelAtPeriodEnd: subscriptionForm.cancelAtPeriodEnd
        }
        if (subscriptionForm.stripeSubscriptionId) {
          updateData.stripeSubscriptionId =
            subscriptionForm.stripeSubscriptionId
        }
        if (subscriptionForm.stripeCustomerId) {
          updateData.stripeCustomerId = subscriptionForm.stripeCustomerId
        }
        await updateSubscription(
          subscriptionForm.existingSubscription.id,
          updateData
        )

        // Also update the office tier and capacity
        await AdminService.updateOfficeTier(officeId, subscriptionForm.tier)
        const newCapacity = Number(subscriptionForm.deskCapacity)
        if (newCapacity > 0) {
          await AdminService.updateOfficeCapacity(officeId, newCapacity)
        }

        await AdminService.logAdminAction(
          currentUserId,
          currentUserEmail,
          'subscription_updated',
          officeId,
          undefined,
          {
            tier: subscriptionForm.tier,
            status: subscriptionForm.status,
            billingInterval: subscriptionForm.billingInterval
          }
        )
      } else {
        // Create new subscription
        const createData: Omit<Database.Subscription, 'id'> = {
          officeId,
          ownerId,
          tier: subscriptionForm.tier,
          status: subscriptionForm.status,
          billingInterval: subscriptionForm.billingInterval,
          currentPeriodStart: new Date(subscriptionForm.currentPeriodStart),
          currentPeriodEnd: new Date(subscriptionForm.currentPeriodEnd),
          cancelAtPeriodEnd: subscriptionForm.cancelAtPeriodEnd,
          createdAt: new Date(),
          updatedAt: new Date()
        }
        if (subscriptionForm.stripeSubscriptionId) {
          createData.stripeSubscriptionId =
            subscriptionForm.stripeSubscriptionId
        }
        if (subscriptionForm.stripeCustomerId) {
          createData.stripeCustomerId = subscriptionForm.stripeCustomerId
        }
        await createSubscription(createData)

        // Also update the office tier and capacity to match
        await AdminService.updateOfficeTier(officeId, subscriptionForm.tier)
        const newCapacity = Number(subscriptionForm.deskCapacity)
        if (newCapacity > 0) {
          await AdminService.updateOfficeCapacity(officeId, newCapacity)
        }

        await AdminService.logAdminAction(
          currentUserId,
          currentUserEmail,
          'subscription_created',
          officeId,
          undefined,
          {
            tier: subscriptionForm.tier,
            status: subscriptionForm.status,
            billingInterval: subscriptionForm.billingInterval
          }
        )
      }

      // Track analytics
      try {
        trackEvent(AnalyticsEvents.ADMIN_OFFICE_CONFIG, {
          action_type: subscriptionForm.existingSubscription
            ? 'update_subscription'
            : 'create_subscription'
        })
      } catch (analyticsError) {
        console.error('Failed to track subscription event:', analyticsError)
      }

      // Update local office state
      setOffices(prevOffices =>
        prevOffices.map(o =>
          o.oid === officeId
            ? {
                ...o,
                subscriptionTier: subscriptionForm.tier,
                userCapacity:
                  Number(subscriptionForm.deskCapacity) || o.userCapacity
              }
            : o
        )
      )

      // Refresh office details cache
      const renewalDate = new Date(
        subscriptionForm.currentPeriodEnd
      ).toLocaleDateString()
      setOfficeDetails(prev => ({
        ...prev,
        [officeId]: {
          ownerEmail: prev[officeId]?.ownerEmail || null,
          billingInterval: subscriptionForm.billingInterval,
          renewalDate: renewalDate === 'Invalid Date' ? null : renewalDate
        }
      }))

      const message = subscriptionForm.existingSubscription
        ? `Successfully updated subscription for ${subscriptionForm.office.name}`
        : `Successfully created subscription for ${subscriptionForm.office.name}`
      setSuccessMessage(message)
      if (onSuccess) onSuccess(message)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save subscription'
      setError(errorMessage)
      if (onError) onError(errorMessage)
    } finally {
      setIsOperating(false)
      handleSubscriptionDialogClose()
    }
  }

  const getTierDisplayName = (
    tier: Database.SubscriptionTier | undefined
  ): string => {
    if (!tier) return 'Free'
    return tier.charAt(0).toUpperCase() + tier.slice(1)
  }

  const getBillingDisplay = (officeId: string | undefined): string => {
    if (!officeId) return 'N/A'
    // Hide billing period for free tier offices
    const office = offices.find(o => o.oid === officeId)
    if (office?.subscriptionTier === 'free' || !office?.subscriptionTier)
      return ''
    if (!officeDetails[officeId]) return 'N/A'
    const details = officeDetails[officeId]
    if (!details.billingInterval) return 'N/A'
    return details.billingInterval === 'monthly' ? 'Monthly' : 'Annual'
  }

  const getRenewalDisplay = (officeId: string | undefined): string => {
    if (!officeId) return 'N/A'
    // Hide renewal date for free tier offices
    const office = offices.find(o => o.oid === officeId)
    if (office?.subscriptionTier === 'free' || !office?.subscriptionTier)
      return ''
    if (!officeDetails[officeId]) return 'N/A'
    const details = officeDetails[officeId]
    return details.renewalDate || 'N/A'
  }

  const getOwnerDisplay = (officeId: string | undefined): string => {
    if (!officeId || !officeDetails[officeId]) return 'N/A'
    const details = officeDetails[officeId]
    return details.ownerEmail || 'N/A'
  }

  if (isLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight={400}
      >
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (error && offices.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error' onClose={() => loadOffices()}>
          {error}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' gutterBottom>
        Office Configuration Management
      </Typography>

      {/* Office List */}
      {isMobile ? (
        // Mobile: Card Layout
        <Box>
          {offices.length === 0 ? (
            <Typography color='textSecondary' align='center' sx={{ py: 4 }}>
              No offices found
            </Typography>
          ) : (
            offices.map(office => (
              <Card key={office.oid} sx={{ mb: 2, opacity: office.isDeleted ? 0.7 : 1 }}>
                <CardContent>
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='flex-start'
                    mb={1}
                  >
                    <Box flex={1}>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Typography variant='h6'>{office.name}</Typography>
                        {office.isDeleted && (
                          <Chip label='Deleted' color='error' size='small' />
                        )}
                      </Box>
                      <Typography variant='body2' color='textSecondary'>
                        Owner: {getOwnerDisplay(office.oid)}
                      </Typography>
                      <Typography variant='body2' color='textSecondary'>
                        Capacity: {office.userCapacity || 0}
                      </Typography>
                      <Typography variant='body2' color='textSecondary'>
                        Tier: {getTierDisplayName(office.subscriptionTier)}
                      </Typography>
                      <Typography variant='body2' color='textSecondary'>
                        Billing: {getBillingDisplay(office.oid)}
                      </Typography>
                      <Typography variant='body2' color='textSecondary'>
                        Renewal: {getRenewalDisplay(office.oid)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display='flex' gap={1} mt={2} flexWrap='wrap'>
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<EditIcon />}
                      onClick={() => handleEditClick(office)}
                      disabled={isOperating}
                    >
                      Edit
                    </Button>
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<PaymentIcon />}
                      onClick={() => handleSubscriptionClick(office)}
                      disabled={isOperating}
                    >
                      Subscription
                    </Button>
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<PeopleIcon />}
                      onClick={() => handleViewAccessClick(office)}
                      disabled={isOperating}
                    >
                      View Access
                    </Button>
                    <Button
                      variant='outlined'
                      size='small'
                      color={office.isDeleted ? 'success' : 'error'}
                      startIcon={office.isDeleted ? <RestoreIcon /> : <DeleteIcon />}
                      onClick={() => handleToggleDeleteClick(office)}
                      disabled={isOperating}
                    >
                      {office.isDeleted ? 'Restore' : 'Delete'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        // Desktop: Table Layout
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Office Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell align='right'>Desk Capacity</TableCell>
                <TableCell>Subscription Tier</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Renewal Date</TableCell>
                <TableCell align='right'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align='center'>
                    <Typography color='textSecondary' sx={{ py: 4 }}>
                      No offices found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                offices.map(office => (
                  <TableRow key={office.oid} sx={{ opacity: office.isDeleted ? 0.7 : 1 }}>
                    <TableCell>{office.name}</TableCell>
                    <TableCell>
                      {office.isDeleted ? (
                        <Chip label='Deleted' color='error' size='small' />
                      ) : (
                        <Chip label='Active' color='success' size='small' />
                      )}
                    </TableCell>
                    <TableCell>{getOwnerDisplay(office.oid)}</TableCell>
                    <TableCell align='right'>
                      {office.userCapacity || 0}
                    </TableCell>
                    <TableCell>
                      {getTierDisplayName(office.subscriptionTier)}
                    </TableCell>
                    <TableCell>{getBillingDisplay(office.oid)}</TableCell>
                    <TableCell>{getRenewalDisplay(office.oid)}</TableCell>
                    <TableCell align='right'>
                      <Box display='flex' gap={1} justifyContent='flex-end'>
                        <Button
                          variant='outlined'
                          size='small'
                          startIcon={<EditIcon />}
                          onClick={() => handleEditClick(office)}
                          disabled={isOperating}
                        >
                          Edit
                        </Button>
                        <Button
                          variant='outlined'
                          size='small'
                          startIcon={<PaymentIcon />}
                          onClick={() => handleSubscriptionClick(office)}
                          disabled={isOperating}
                        >
                          Subscription
                        </Button>
                        <Button
                          variant='outlined'
                          size='small'
                          startIcon={<PeopleIcon />}
                          onClick={() => handleViewAccessClick(office)}
                          disabled={isOperating}
                        >
                          View Access
                        </Button>
                        <Tooltip title={office.isDeleted ? 'Restore this office so it becomes visible to users again' : 'Soft-delete this office to hide it from users'}>
                          <Button
                            variant='outlined'
                            size='small'
                            color={office.isDeleted ? 'success' : 'error'}
                            startIcon={office.isDeleted ? <RestoreIcon /> : <DeleteIcon />}
                            onClick={() => handleToggleDeleteClick(office)}
                            disabled={isOperating}
                          >
                            {office.isDeleted ? 'Restore' : 'Delete'}
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Access List Dialog */}
      <Dialog
        open={accessDialogOpen}
        onClose={handleAccessDialogClose}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Office Access - {accessDialogOffice?.name}</DialogTitle>
        <DialogContent>
          {isLoadingAccess ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : accessList.length === 0 ? (
            <Typography color='textSecondary' align='center' sx={{ py: 4 }}>
              No users found with access to this office
            </Typography>
          ) : (
            <List>
              {accessList.map(entry => (
                <ListItem key={entry.user.id || entry.user.uid}>
                  <ListItemText
                    primary={
                      entry.user.email ||
                      `${entry.user.firstname || ''} ${
                        entry.user.lastname || ''
                      }`.trim() ||
                      'Unknown user'
                    }
                    secondary={
                      entry.user.firstname && entry.user.lastname
                        ? `${entry.user.firstname} ${entry.user.lastname}`
                        : undefined
                    }
                  />
                  <Chip
                    label={
                      entry.role.charAt(0).toUpperCase() + entry.role.slice(1)
                    }
                    color={getRoleColor(entry.role)}
                    size='small'
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAccessDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Edit Office Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant='subtitle1' gutterBottom>
              Office: {editState.office?.name}
            </Typography>

            <TextField
              fullWidth
              label='Desk Capacity'
              type='number'
              value={editState.capacity}
              onChange={e => handleCapacityChange(e.target.value)}
              error={!!editState.capacityError}
              helperText={editState.capacityError || 'Enter a positive integer'}
              sx={{ mb: 3, mt: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel id='tier-select-label'>Subscription Tier</InputLabel>
              <Select
                labelId='tier-select-label'
                value={editState.tier}
                label='Subscription Tier'
                onChange={e =>
                  handleTierChange(e.target.value as Database.SubscriptionTier)
                }
              >
                <MenuItem value='free'>Free</MenuItem>
                <MenuItem value='small'>Small</MenuItem>
                <MenuItem value='medium'>Medium</MenuItem>
                <MenuItem value='large'>Large</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={handleSaveClick}
            variant='contained'
            color='primary'
            disabled={!!editState.capacityError}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog
        open={subscriptionDialogOpen}
        onClose={handleSubscriptionDialogClose}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {subscriptionForm.existingSubscription
            ? 'Edit Subscription'
            : 'Create Subscription'}
          {subscriptionForm.office && ` - ${subscriptionForm.office.name}`}
        </DialogTitle>
        <DialogContent>
          {isLoadingSubscription ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <FormControl fullWidth>
                <InputLabel id='sub-tier-label'>Tier</InputLabel>
                <Select
                  labelId='sub-tier-label'
                  value={subscriptionForm.tier}
                  label='Tier'
                  onChange={e => {
                    const newTier = e.target.value as Database.SubscriptionTier
                    setSubscriptionForm(prev => ({
                      ...prev,
                      tier: newTier,
                      deskCapacity: String(TIER_CAPACITIES[newTier])
                    }))
                  }}
                >
                  <MenuItem value='free'>Free</MenuItem>
                  <MenuItem value='small'>Small</MenuItem>
                  <MenuItem value='medium'>Medium</MenuItem>
                  <MenuItem value='large'>Large</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label='Desk Capacity'
                type='number'
                value={subscriptionForm.deskCapacity}
                onChange={e =>
                  setSubscriptionForm(prev => ({
                    ...prev,
                    deskCapacity: e.target.value
                  }))
                }
                helperText={`Recommended for ${subscriptionForm.tier} tier: ${
                  TIER_CAPACITIES[subscriptionForm.tier]
                }`}
              />

              <FormControl fullWidth>
                <InputLabel id='sub-status-label'>Status</InputLabel>
                <Select
                  labelId='sub-status-label'
                  value={subscriptionForm.status}
                  label='Status'
                  onChange={e =>
                    setSubscriptionForm(prev => ({
                      ...prev,
                      status: e.target.value as Database.SubscriptionStatus
                    }))
                  }
                >
                  <MenuItem value='active'>Active</MenuItem>
                  <MenuItem value='canceled'>Canceled</MenuItem>
                  <MenuItem value='past_due'>Past Due</MenuItem>
                  <MenuItem value='unpaid'>Unpaid</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id='sub-billing-label'>Billing Interval</InputLabel>
                <Select
                  labelId='sub-billing-label'
                  value={subscriptionForm.billingInterval}
                  label='Billing Interval'
                  onChange={e =>
                    setSubscriptionForm(prev => ({
                      ...prev,
                      billingInterval: e.target.value as 'monthly' | 'annual'
                    }))
                  }
                >
                  <MenuItem value='monthly'>Monthly</MenuItem>
                  <MenuItem value='annual'>Annual</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label='Period Start'
                type='date'
                value={subscriptionForm.currentPeriodStart}
                onChange={e =>
                  setSubscriptionForm(prev => ({
                    ...prev,
                    currentPeriodStart: e.target.value
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label='Period End (Expiry)'
                type='date'
                value={subscriptionForm.currentPeriodEnd}
                onChange={e =>
                  setSubscriptionForm(prev => ({
                    ...prev,
                    currentPeriodEnd: e.target.value
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />

              <FormControl fullWidth>
                <InputLabel id='sub-cancel-label'>
                  Cancel at Period End
                </InputLabel>
                <Select
                  labelId='sub-cancel-label'
                  value={subscriptionForm.cancelAtPeriodEnd ? 'yes' : 'no'}
                  label='Cancel at Period End'
                  onChange={e =>
                    setSubscriptionForm(prev => ({
                      ...prev,
                      cancelAtPeriodEnd: e.target.value === 'yes'
                    }))
                  }
                >
                  <MenuItem value='no'>No</MenuItem>
                  <MenuItem value='yes'>Yes</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label='Stripe Subscription ID (optional)'
                value={subscriptionForm.stripeSubscriptionId}
                onChange={e =>
                  setSubscriptionForm(prev => ({
                    ...prev,
                    stripeSubscriptionId: e.target.value
                  }))
                }
              />

              <TextField
                fullWidth
                label='Stripe Customer ID (optional)'
                value={subscriptionForm.stripeCustomerId}
                onChange={e =>
                  setSubscriptionForm(prev => ({
                    ...prev,
                    stripeCustomerId: e.target.value
                  }))
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubscriptionDialogClose} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSubscription}
            variant='contained'
            color='primary'
            disabled={
              isLoadingSubscription ||
              !subscriptionForm.currentPeriodStart ||
              !subscriptionForm.currentPeriodEnd
            }
          >
            {subscriptionForm.existingSubscription
              ? 'Update Subscription'
              : 'Create Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity={confirmDialog.severity}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity='success'
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error && offices.length > 0}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setError(null)}
          severity='error'
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default OfficeConfigManagement
